<script lang="ts">
    import { onDestroy } from "svelte";
    import { LL } from "../../../../i18n/i18n-svelte";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import { availabilityStatusStore, inBbbStore, inJitsiStore, inLivekitStore } from "../../../Stores/MediaStore";
    import { openedMenuStore } from "../../../Stores/MenuStore";
    import { isInRemoteConversation } from "../../../Stores/StreamableCollectionStore";
    import { getColorHexOfStatus, getStatusLabel } from "../../../Utils/AvailabilityStatus";
    import { showFloatingUi } from "../../../Utils/svelte-floatingui-show";
    import ChevronDownIcon from "../../Icons/ChevronDownIcon.svelte";
    import MenuBurgerIcon from "../../Icons/MenuBurgerIcon.svelte";
    import XIcon from "../../Icons/XIcon.svelte";
    import Woka from "../../Woka/WokaFromUserId.svelte";
    import ProfileMenuContent from "./ProfileMenuContent.svelte";

    const PROFILE_MENU_Z_INDEX = 1100;

    interface Props {
        compact?: boolean;
    }

    let { compact = false }: Props = $props();

    let userName = gameManager.getPlayerName() || "";
    let forceBurgerMode = $derived($inJitsiStore || $inBbbStore || $inLivekitStore || $isInRemoteConversation);
    let triggerElement: HTMLButtonElement | undefined = $state();
    let contextualMenuItemsMarker: HTMLSpanElement | undefined = $state();
    let closeFloatingUi: (() => void) | undefined;

    function closeProfileMenu(): void {
        const close = closeFloatingUi;
        closeFloatingUi = undefined;
        close?.();
    }

    function openProfileMenu(): void {
        if (triggerElement === undefined || closeFloatingUi !== undefined) {
            return;
        }

        closeFloatingUi = showFloatingUi(
            triggerElement,
            ProfileMenuContent,
            {
                showContextualMenuItems:
                    contextualMenuItemsMarker !== undefined &&
                    getComputedStyle(contextualMenuItemsMarker).display !== "none",
            },
            {
                placement: compact ? "top-start" : "bottom-end",
            },
            8,
            true,
            true,
            () => {
                closeFloatingUi = undefined;
                openedMenuStore.close("profileMenu");
            },
            PROFILE_MENU_Z_INDEX,
        );
    }

    $effect(() => {
        if ($openedMenuStore === "profileMenu") {
            openProfileMenu();
        } else {
            closeProfileMenu();
        }
    });

    onDestroy(closeProfileMenu);
</script>

<div data-testid="action-user" class="flex items-center transition-all pointer-events-auto">
    <button
        type="button"
        aria-label={$LL.menu.sub.profile()}
        aria-haspopup="menu"
        aria-expanded={$openedMenuStore === "profileMenu"}
        class={compact
            ? "nutrivera-action-button group relative m-1 flex h-10 w-10 cursor-pointer items-center justify-center rounded-[10px] border-0 bg-[var(--nutrivera-shell-raised)] p-0 text-start shadow-none hover:bg-[var(--nutrivera-shell-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--nutrivera-shell-accent)]"
            : "nutrivera-shell-surface group h-11 cursor-pointer rounded-[var(--nutrivera-shell-radius)] border border-solid border-[var(--nutrivera-shell-border)] bg-[var(--nutrivera-shell-bg)] p-1 text-start shadow-[0_6px_8px_rgba(7,17,28,0.28)] hover:bg-[var(--nutrivera-shell-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--nutrivera-shell-accent)]"}
        class:profile-menu-force-burger={forceBurgerMode}
        bind:this={triggerElement}
        onclick={(event) => {
            event.preventDefault();
            openedMenuStore.toggle("profileMenu");
        }}
    >
        <span
            class="absolute size-0 pointer-events-none sm:hidden"
            aria-hidden="true"
            bind:this={contextualMenuItemsMarker}
        ></span>
        {#if compact}
            <span class="relative flex size-8 items-center justify-center overflow-hidden rounded-full">
                <Woka userId={-1} placeholderSrc="" customWidth="28px" />
                <span
                    class="absolute bottom-0 end-0 size-2.5 rounded-full border-2 border-[var(--nutrivera-shell-raised)]"
                    style="background-color: {getColorHexOfStatus($availabilityStatusStore)}"
                ></span>
            </span>
        {:else}
            <div
                class="profile-menu-burger-icon h-12 w-12 sm:h-10 sm:w-10 xl:h-12 xl:w-12 p-1 m-0 items-center justify-center flex md:hidden"
            >
                {#if $openedMenuStore !== "profileMenu"}
                    <MenuBurgerIcon classList="pointer-events-none" />
                {:else}
                    <XIcon classList="pointer-events-none" />
                {/if}
            </div>
            <div
                class="profile-menu-full hidden md:flex items-center h-full group-hover:bg-white/10 transition-all group-hover:rounded gap-2 pl-0 pr-3"
            >
                <div class="overflow-hidden p-2 flex items-center justify-center rounded h-full aspect-square relative">
                    <Woka userId={-1} placeholderSrc="" customWidth="30px" />
                </div>
                <div class="grow flex flex-row xl:flex-col justify-start text-start pr-2">
                    <div
                        class="font-bold text-white leading-5 whitespace-nowrap select-none text-base sm:text-sm xl:text-base order-last xl:order-first flex items-center"
                    >
                        {userName}
                    </div>
                    <div class="text-xxs bold whitespace-nowrap select-none flex items-center">
                        <div
                            class="aspect-square h-2 w-2 rounded-full me-1.5"
                            style="background-color: {getColorHexOfStatus($availabilityStatusStore)}"
                        ></div>
                        <div
                            class="hidden xl:block"
                            style="color: {getColorHexOfStatus($availabilityStatusStore)};filter: brightness(200%);"
                        >
                            {getStatusLabel($availabilityStatusStore)}
                        </div>
                    </div>
                </div>
                <div>
                    <ChevronDownIcon
                        strokeWidth="2"
                        classList="transition-all opacity-50 {$openedMenuStore === 'profileMenu' ? 'rotate-180' : ''}"
                        height="h-4"
                        width="w-4"
                    />
                </div>
            </div>
        {/if}
    </button>
</div>

<style>
    /* Force burger mode when in a meeting or conversation (more compact profile button) */
    :global(.profile-menu-force-burger .profile-menu-burger-icon) {
        display: flex !important;
    }
    :global(.profile-menu-force-burger .profile-menu-full) {
        display: none !important;
    }
</style>
